function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { isThenable, uuid4, dateTimestampInSeconds, consoleSandbox, logger, GLOBAL_OBJ, getGlobalSingleton } from '@sentry/utils';
import { DEFAULT_ENVIRONMENT } from './constants.js';
import { DEBUG_BUILD } from './debug-build.js';
import { Scope } from './scope.js';
import { closeSession, makeSession, updateSession } from './session.js';
import { SDK_VERSION } from './version.js';

/**
 * API compatibility version of this hub.
 *
 * WARNING: This number should only be increased when the global interface
 * changes and new methods are introduced.
 *
 * @hidden
 */
var API_VERSION = parseFloat(SDK_VERSION);

/**
 * Default maximum number of breadcrumbs added to an event. Can be overwritten
 * with {@link Options.maxBreadcrumbs}.
 */
var DEFAULT_BREADCRUMBS = 100;

/**
 * @inheritDoc
 */
var Hub = /*#__PURE__*/function () {
  /** Is a {@link Layer}[] containing the client and scope */

  /** Contains the last event id of a captured event.  */

  /**
   * Creates a new instance of the hub, will push one {@link Layer} into the
   * internal stack on creation.
   *
   * @param client bound to the hub.
   * @param scope bound to the hub.
   * @param version number, higher number means higher priority.
   *
   * @deprecated Instantiation of Hub objects is deprecated and the constructor will be removed in version 8 of the SDK.
   *
   * If you are currently using the Hub for multi-client use like so:
   *
   * ```
   * // OLD
   * const hub = new Hub();
   * hub.bindClient(client);
   * makeMain(hub)
   * ```
   *
   * instead initialize the client as follows:
   *
   * ```
   * // NEW
   * Sentry.withIsolationScope(() => {
   *    Sentry.setCurrentClient(client);
   *    client.init();
   * });
   * ```
   *
   * If you are using the Hub to capture events like so:
   *
   * ```
   * // OLD
   * const client = new Client();
   * const hub = new Hub(client);
   * hub.captureException()
   * ```
   *
   * instead capture isolated events as follows:
   *
   * ```
   * // NEW
   * const client = new Client();
   * const scope = new Scope();
   * scope.setClient(client);
   * scope.captureException();
   * ```
   */
  function Hub(client, scope, isolationScope) {
    var _version = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : API_VERSION;
    _classCallCheck(this, Hub);
    this._version = _version;
    var assignedScope;
    if (!scope) {
      assignedScope = new Scope();
      assignedScope.setClient(client);
    } else {
      assignedScope = scope;
    }
    var assignedIsolationScope;
    if (!isolationScope) {
      assignedIsolationScope = new Scope();
      assignedIsolationScope.setClient(client);
    } else {
      assignedIsolationScope = isolationScope;
    }
    this._stack = [{
      scope: assignedScope
    }];
    if (client) {
      // eslint-disable-next-line deprecation/deprecation
      this.bindClient(client);
    }
    this._isolationScope = assignedIsolationScope;
  }

  /**
   * Checks if this hub's version is older than the given version.
   *
   * @param version A version number to compare to.
   * @return True if the given version is newer; otherwise false.
   *
   * @deprecated This will be removed in v8.
   */
  _createClass(Hub, [{
    key: "isOlderThan",
    value: function isOlderThan(version) {
      return this._version < version;
    }
    /**
     * This binds the given client to the current scope.
     * @param client An SDK client (client) instance.
     *
     * @deprecated Use `initAndBind()` directly, or `setCurrentClient()` and/or `client.init()` instead.
     */
  }, {
    key: "bindClient",
    value: function bindClient(client) {
      // eslint-disable-next-line deprecation/deprecation
      var top = this.getStackTop();
      top.client = client;
      top.scope.setClient(client);
      // eslint-disable-next-line deprecation/deprecation
      if (client && client.setupIntegrations) {
        // eslint-disable-next-line deprecation/deprecation
        client.setupIntegrations();
      }
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `withScope` instead.
     */
  }, {
    key: "pushScope",
    value: function pushScope() {
      // We want to clone the content of prev scope
      // eslint-disable-next-line deprecation/deprecation
      var scope = this.getScope().clone();
      // eslint-disable-next-line deprecation/deprecation
      this.getStack().push({
        // eslint-disable-next-line deprecation/deprecation
        client: this.getClient(),
        scope: scope
      });
      return scope;
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `withScope` instead.
     */
  }, {
    key: "popScope",
    value: function popScope() {
      // eslint-disable-next-line deprecation/deprecation
      if (this.getStack().length <= 1) return false;
      // eslint-disable-next-line deprecation/deprecation
      return !!this.getStack().pop();
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `Sentry.withScope()` instead.
     */
  }, {
    key: "withScope",
    value: function withScope(callback) {
      var _this = this;
      // eslint-disable-next-line deprecation/deprecation
      var scope = this.pushScope();
      var maybePromiseResult;
      try {
        maybePromiseResult = callback(scope);
      } catch (e) {
        // eslint-disable-next-line deprecation/deprecation
        this.popScope();
        throw e;
      }
      if (isThenable(maybePromiseResult)) {
        // @ts-expect-error - isThenable returns the wrong type
        return maybePromiseResult.then(function (res) {
          // eslint-disable-next-line deprecation/deprecation
          _this.popScope();
          return res;
        }, function (e) {
          // eslint-disable-next-line deprecation/deprecation
          _this.popScope();
          throw e;
        });
      }

      // eslint-disable-next-line deprecation/deprecation
      this.popScope();
      return maybePromiseResult;
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `Sentry.getClient()` instead.
     */
  }, {
    key: "getClient",
    value: function getClient() {
      // eslint-disable-next-line deprecation/deprecation
      return this.getStackTop().client;
    }
    /**
     * Returns the scope of the top stack.
     *
     * @deprecated Use `Sentry.getCurrentScope()` instead.
     */
  }, {
    key: "getScope",
    value: function getScope() {
      // eslint-disable-next-line deprecation/deprecation
      return this.getStackTop().scope;
    }
    /**
     * @deprecated Use `Sentry.getIsolationScope()` instead.
     */
  }, {
    key: "getIsolationScope",
    value: function getIsolationScope() {
      return this._isolationScope;
    }
    /**
     * Returns the scope stack for domains or the process.
     * @deprecated This will be removed in v8.
     */
  }, {
    key: "getStack",
    value: function getStack() {
      return this._stack;
    }
    /**
     * Returns the topmost scope layer in the order domain > local > process.
     * @deprecated This will be removed in v8.
     */
  }, {
    key: "getStackTop",
    value: function getStackTop() {
      return this._stack[this._stack.length - 1];
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `Sentry.captureException()` instead.
     */
  }, {
    key: "captureException",
    value: function captureException(exception, hint) {
      var eventId = this._lastEventId = hint && hint.event_id ? hint.event_id : uuid4();
      var syntheticException = new Error('Sentry syntheticException');
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().captureException(exception, _objectSpread(_objectSpread({
        originalException: exception,
        syntheticException: syntheticException
      }, hint), {}, {
        event_id: eventId
      }));
      return eventId;
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use  `Sentry.captureMessage()` instead.
     */
  }, {
    key: "captureMessage",
    value: function captureMessage(message,
    // eslint-disable-next-line deprecation/deprecation
    level, hint) {
      var eventId = this._lastEventId = hint && hint.event_id ? hint.event_id : uuid4();
      var syntheticException = new Error(message);
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().captureMessage(message, level, _objectSpread(_objectSpread({
        originalException: message,
        syntheticException: syntheticException
      }, hint), {}, {
        event_id: eventId
      }));
      return eventId;
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `Sentry.captureEvent()` instead.
     */
  }, {
    key: "captureEvent",
    value: function captureEvent(event, hint) {
      var eventId = hint && hint.event_id ? hint.event_id : uuid4();
      if (!event.type) {
        this._lastEventId = eventId;
      }
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().captureEvent(event, _objectSpread(_objectSpread({}, hint), {}, {
        event_id: eventId
      }));
      return eventId;
    }
    /**
     * @inheritDoc
     *
     * @deprecated This will be removed in v8.
     */
  }, {
    key: "lastEventId",
    value: function lastEventId() {
      return this._lastEventId;
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `Sentry.addBreadcrumb()` instead.
     */
  }, {
    key: "addBreadcrumb",
    value: function addBreadcrumb(breadcrumb, hint) {
      // eslint-disable-next-line deprecation/deprecation
      var _this$getStackTop = this.getStackTop(),
        scope = _this$getStackTop.scope,
        client = _this$getStackTop.client;
      if (!client) return;
      var _ref = client.getOptions && client.getOptions() || {},
        _ref$beforeBreadcrumb = _ref.beforeBreadcrumb,
        beforeBreadcrumb = _ref$beforeBreadcrumb === void 0 ? null : _ref$beforeBreadcrumb,
        _ref$maxBreadcrumbs = _ref.maxBreadcrumbs,
        maxBreadcrumbs = _ref$maxBreadcrumbs === void 0 ? DEFAULT_BREADCRUMBS : _ref$maxBreadcrumbs;
      if (maxBreadcrumbs <= 0) return;
      var timestamp = dateTimestampInSeconds();
      var mergedBreadcrumb = _objectSpread({
        timestamp: timestamp
      }, breadcrumb);
      var finalBreadcrumb = beforeBreadcrumb ? consoleSandbox(function () {
        return beforeBreadcrumb(mergedBreadcrumb, hint);
      }) : mergedBreadcrumb;
      if (finalBreadcrumb === null) return;
      if (client.emit) {
        client.emit('beforeAddBreadcrumb', finalBreadcrumb, hint);
      }

      // TODO(v8): I know this comment doesn't make much sense because the hub will be deprecated but I still wanted to
      // write it down. In theory, we would have to add the breadcrumbs to the isolation scope here, however, that would
      // duplicate all of the breadcrumbs. There was the possibility of adding breadcrumbs to both, the isolation scope
      // and the normal scope, and deduplicating it down the line in the event processing pipeline. However, that would
      // have been very fragile, because the breadcrumb objects would have needed to keep their identity all throughout
      // the event processing pipeline.
      // In the new implementation, the top level `Sentry.addBreadcrumb()` should ONLY write to the isolation scope.

      scope.addBreadcrumb(finalBreadcrumb, maxBreadcrumbs);
    }
    /**
     * @inheritDoc
     * @deprecated Use `Sentry.setUser()` instead.
     */
  }, {
    key: "setUser",
    value: function setUser(user) {
      // TODO(v8): The top level `Sentry.setUser()` function should write ONLY to the isolation scope.
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().setUser(user);
      // eslint-disable-next-line deprecation/deprecation
      this.getIsolationScope().setUser(user);
    }
    /**
     * @inheritDoc
     * @deprecated Use `Sentry.setTags()` instead.
     */
  }, {
    key: "setTags",
    value: function setTags(tags) {
      // TODO(v8): The top level `Sentry.setTags()` function should write ONLY to the isolation scope.
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().setTags(tags);
      // eslint-disable-next-line deprecation/deprecation
      this.getIsolationScope().setTags(tags);
    }
    /**
     * @inheritDoc
     * @deprecated Use `Sentry.setExtras()` instead.
     */
  }, {
    key: "setExtras",
    value: function setExtras(extras) {
      // TODO(v8): The top level `Sentry.setExtras()` function should write ONLY to the isolation scope.
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().setExtras(extras);
      // eslint-disable-next-line deprecation/deprecation
      this.getIsolationScope().setExtras(extras);
    }
    /**
     * @inheritDoc
     * @deprecated Use `Sentry.setTag()` instead.
     */
  }, {
    key: "setTag",
    value: function setTag(key, value) {
      // TODO(v8): The top level `Sentry.setTag()` function should write ONLY to the isolation scope.
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().setTag(key, value);
      // eslint-disable-next-line deprecation/deprecation
      this.getIsolationScope().setTag(key, value);
    }
    /**
     * @inheritDoc
     * @deprecated Use `Sentry.setExtra()` instead.
     */
  }, {
    key: "setExtra",
    value: function setExtra(key, extra) {
      // TODO(v8): The top level `Sentry.setExtra()` function should write ONLY to the isolation scope.
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().setExtra(key, extra);
      // eslint-disable-next-line deprecation/deprecation
      this.getIsolationScope().setExtra(key, extra);
    }
    /**
     * @inheritDoc
     * @deprecated Use `Sentry.setContext()` instead.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {
    key: "setContext",
    value: function setContext(name, context) {
      // TODO(v8): The top level `Sentry.setContext()` function should write ONLY to the isolation scope.
      // eslint-disable-next-line deprecation/deprecation
      this.getScope().setContext(name, context);
      // eslint-disable-next-line deprecation/deprecation
      this.getIsolationScope().setContext(name, context);
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use `getScope()` directly.
     */
  }, {
    key: "configureScope",
    value: function configureScope(callback) {
      // eslint-disable-next-line deprecation/deprecation
      var _this$getStackTop2 = this.getStackTop(),
        scope = _this$getStackTop2.scope,
        client = _this$getStackTop2.client;
      if (client) {
        callback(scope);
      }
    }
    /**
     * @inheritDoc
     */
  }, {
    key: "run",
    value: function run(callback) {
      // eslint-disable-next-line deprecation/deprecation
      var oldHub = makeMain(this);
      try {
        callback(this);
      } finally {
        // eslint-disable-next-line deprecation/deprecation
        makeMain(oldHub);
      }
    }
    /**
     * @inheritDoc
     * @deprecated Use `Sentry.getClient().getIntegrationByName()` instead.
     */
  }, {
    key: "getIntegration",
    value: function getIntegration(integration) {
      // eslint-disable-next-line deprecation/deprecation
      var client = this.getClient();
      if (!client) return null;
      try {
        // eslint-disable-next-line deprecation/deprecation
        return client.getIntegration(integration);
      } catch (_oO) {
        DEBUG_BUILD && logger.warn("Cannot retrieve integration ".concat(integration.id, " from the current Hub"));
        return null;
      }
    }
    /**
     * Starts a new `Transaction` and returns it. This is the entry point to manual tracing instrumentation.
     *
     * A tree structure can be built by adding child spans to the transaction, and child spans to other spans. To start a
     * new child span within the transaction or any span, call the respective `.startChild()` method.
     *
     * Every child span must be finished before the transaction is finished, otherwise the unfinished spans are discarded.
     *
     * The transaction must be finished with a call to its `.end()` method, at which point the transaction with all its
     * finished child spans will be sent to Sentry.
     *
     * @param context Properties of the new `Transaction`.
     * @param customSamplingContext Information given to the transaction sampling function (along with context-dependent
     * default values). See {@link Options.tracesSampler}.
     *
     * @returns The transaction which was just started
     *
     * @deprecated Use `startSpan()`, `startSpanManual()` or `startInactiveSpan()` instead.
     */
  }, {
    key: "startTransaction",
    value: function startTransaction(context, customSamplingContext) {
      var result = this._callExtensionMethod('startTransaction', context, customSamplingContext);
      if (DEBUG_BUILD && !result) {
        // eslint-disable-next-line deprecation/deprecation
        var client = this.getClient();
        if (!client) {
          logger.warn("Tracing extension 'startTransaction' is missing. You should 'init' the SDK before calling 'startTransaction'");
        } else {
          logger.warn("Tracing extension 'startTransaction' has not been added. Call 'addTracingExtensions' before calling 'init':\nSentry.addTracingExtensions();\nSentry.init({...});\n");
        }
      }
      return result;
    }
    /**
     * @inheritDoc
     * @deprecated Use `spanToTraceHeader()` instead.
     */
  }, {
    key: "traceHeaders",
    value: function traceHeaders() {
      return this._callExtensionMethod('traceHeaders');
    }
    /**
     * @inheritDoc
     *
     * @deprecated Use top level `captureSession` instead.
     */
  }, {
    key: "captureSession",
    value: function captureSession() {
      var endSession = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      // both send the update and pull the session from the scope
      if (endSession) {
        // eslint-disable-next-line deprecation/deprecation
        return this.endSession();
      }

      // only send the update
      this._sendSessionUpdate();
    }
    /**
     * @inheritDoc
     * @deprecated Use top level `endSession` instead.
     */
  }, {
    key: "endSession",
    value: function endSession() {
      // eslint-disable-next-line deprecation/deprecation
      var layer = this.getStackTop();
      var scope = layer.scope;
      var session = scope.getSession();
      if (session) {
        closeSession(session);
      }
      this._sendSessionUpdate();

      // the session is over; take it off of the scope
      scope.setSession();
    }
    /**
     * @inheritDoc
     * @deprecated Use top level `startSession` instead.
     */
  }, {
    key: "startSession",
    value: function startSession(context) {
      // eslint-disable-next-line deprecation/deprecation
      var _this$getStackTop3 = this.getStackTop(),
        scope = _this$getStackTop3.scope,
        client = _this$getStackTop3.client;
      var _ref2 = client && client.getOptions() || {},
        release = _ref2.release,
        _ref2$environment = _ref2.environment,
        environment = _ref2$environment === void 0 ? DEFAULT_ENVIRONMENT : _ref2$environment;

      // Will fetch userAgent if called from browser sdk
      var _ref3 = GLOBAL_OBJ.navigator || {},
        userAgent = _ref3.userAgent;
      var session = makeSession(_objectSpread(_objectSpread({
        release: release,
        environment: environment,
        user: scope.getUser()
      }, userAgent && {
        userAgent: userAgent
      }), context));

      // End existing session if there's one
      var currentSession = scope.getSession && scope.getSession();
      if (currentSession && currentSession.status === 'ok') {
        updateSession(currentSession, {
          status: 'exited'
        });
      }
      // eslint-disable-next-line deprecation/deprecation
      this.endSession();

      // Afterwards we set the new session on the scope
      scope.setSession(session);
      return session;
    }
    /**
     * Returns if default PII should be sent to Sentry and propagated in ourgoing requests
     * when Tracing is used.
     *
     * @deprecated Use top-level `getClient().getOptions().sendDefaultPii` instead. This function
     * only unnecessarily increased API surface but only wrapped accessing the option.
     */
  }, {
    key: "shouldSendDefaultPii",
    value: function shouldSendDefaultPii() {
      // eslint-disable-next-line deprecation/deprecation
      var client = this.getClient();
      var options = client && client.getOptions();
      return Boolean(options && options.sendDefaultPii);
    }
    /**
     * Sends the current Session on the scope
     */
  }, {
    key: "_sendSessionUpdate",
    value: function _sendSessionUpdate() {
      // eslint-disable-next-line deprecation/deprecation
      var _this$getStackTop4 = this.getStackTop(),
        scope = _this$getStackTop4.scope,
        client = _this$getStackTop4.client;
      var session = scope.getSession();
      if (session && client && client.captureSession) {
        client.captureSession(session);
      }
    }
    /**
     * Calls global extension method and binding current instance to the function call
     */
    // @ts-expect-error Function lacks ending return statement and return type does not include 'undefined'. ts(2366)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {
    key: "_callExtensionMethod",
    value: function _callExtensionMethod(method) {
      var carrier = getMainCarrier();
      var sentry = carrier.__SENTRY__;
      if (sentry && sentry.extensions && typeof sentry.extensions[method] === 'function') {
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }
        return sentry.extensions[method].apply(this, args);
      }
      DEBUG_BUILD && logger.warn("Extension method ".concat(method, " couldn't be found, doing nothing."));
    }
  }]);
  return Hub;
}();
/**
 * Returns the global shim registry.
 *
 * FIXME: This function is problematic, because despite always returning a valid Carrier,
 * it has an optional `__SENTRY__` property, which then in turn requires us to always perform an unnecessary check
 * at the call-site. We always access the carrier through this function, so we can guarantee that `__SENTRY__` is there.
 **/
function getMainCarrier() {
  GLOBAL_OBJ.__SENTRY__ = GLOBAL_OBJ.__SENTRY__ || {
    extensions: {},
    hub: undefined
  };
  return GLOBAL_OBJ;
}

/**
 * Replaces the current main hub with the passed one on the global object
 *
 * @returns The old replaced hub
 *
 * @deprecated Use `setCurrentClient()` instead.
 */
function makeMain(hub) {
  var registry = getMainCarrier();
  var oldHub = getHubFromCarrier(registry);
  setHubOnCarrier(registry, hub);
  return oldHub;
}

/**
 * Returns the default hub instance.
 *
 * If a hub is already registered in the global carrier but this module
 * contains a more recent version, it replaces the registered version.
 * Otherwise, the currently registered hub will be returned.
 *
 * @deprecated Use the respective replacement method directly instead.
 */
function getCurrentHub() {
  // Get main carrier (global for every environment)
  var registry = getMainCarrier();
  if (registry.__SENTRY__ && registry.__SENTRY__.acs) {
    var hub = registry.__SENTRY__.acs.getCurrentHub();
    if (hub) {
      return hub;
    }
  }

  // Return hub that lives on a global object
  return getGlobalHub(registry);
}

/**
 * Get the currently active isolation scope.
 * The isolation scope is active for the current exection context,
 * meaning that it will remain stable for the same Hub.
 */
function getIsolationScope() {
  // eslint-disable-next-line deprecation/deprecation
  return getCurrentHub().getIsolationScope();
}
function getGlobalHub() {
  var registry = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getMainCarrier();
  // If there's no hub, or its an old API, assign a new one

  if (!hasHubOnCarrier(registry) ||
  // eslint-disable-next-line deprecation/deprecation
  getHubFromCarrier(registry).isOlderThan(API_VERSION)) {
    // eslint-disable-next-line deprecation/deprecation
    setHubOnCarrier(registry, new Hub());
  }

  // Return hub that lives on a global object
  return getHubFromCarrier(registry);
}

/**
 * @private Private API with no semver guarantees!
 *
 * If the carrier does not contain a hub, a new hub is created with the global hub client and scope.
 */
function ensureHubOnCarrier(carrier) {
  var parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : getGlobalHub();
  // If there's no hub on current domain, or it's an old API, assign a new one
  if (!hasHubOnCarrier(carrier) ||
  // eslint-disable-next-line deprecation/deprecation
  getHubFromCarrier(carrier).isOlderThan(API_VERSION)) {
    // eslint-disable-next-line deprecation/deprecation
    var client = parent.getClient();
    // eslint-disable-next-line deprecation/deprecation
    var scope = parent.getScope();
    // eslint-disable-next-line deprecation/deprecation
    var isolationScope = parent.getIsolationScope();
    // eslint-disable-next-line deprecation/deprecation
    setHubOnCarrier(carrier, new Hub(client, scope.clone(), isolationScope.clone()));
  }
}

/**
 * @private Private API with no semver guarantees!
 *
 * Sets the global async context strategy
 */
function setAsyncContextStrategy(strategy) {
  // Get main carrier (global for every environment)
  var registry = getMainCarrier();
  registry.__SENTRY__ = registry.__SENTRY__ || {};
  registry.__SENTRY__.acs = strategy;
}

/**
 * Runs the supplied callback in its own async context. Async Context strategies are defined per SDK.
 *
 * @param callback The callback to run in its own async context
 * @param options Options to pass to the async context strategy
 * @returns The result of the callback
 */
function runWithAsyncContext(callback) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var registry = getMainCarrier();
  if (registry.__SENTRY__ && registry.__SENTRY__.acs) {
    return registry.__SENTRY__.acs.runWithAsyncContext(callback, options);
  }

  // if there was no strategy, fallback to just calling the callback
  return callback();
}

/**
 * This will tell whether a carrier has a hub on it or not
 * @param carrier object
 */
function hasHubOnCarrier(carrier) {
  return !!(carrier && carrier.__SENTRY__ && carrier.__SENTRY__.hub);
}

/**
 * This will create a new {@link Hub} and add to the passed object on
 * __SENTRY__.hub.
 * @param carrier object
 * @hidden
 */
function getHubFromCarrier(carrier) {
  // eslint-disable-next-line deprecation/deprecation
  return getGlobalSingleton('hub', function () {
    return new Hub();
  }, carrier);
}

/**
 * This will set passed {@link Hub} on the passed object's __SENTRY__.hub attribute
 * @param carrier object
 * @param hub Hub
 * @returns A boolean indicating success or failure
 */
function setHubOnCarrier(carrier, hub) {
  if (!carrier) return false;
  var __SENTRY__ = carrier.__SENTRY__ = carrier.__SENTRY__ || {};
  __SENTRY__.hub = hub;
  return true;
}
export { API_VERSION, Hub, ensureHubOnCarrier, getCurrentHub, getHubFromCarrier, getIsolationScope, getMainCarrier, makeMain, runWithAsyncContext, setAsyncContextStrategy, setHubOnCarrier };